"use client";

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { Building2, Users, Activity, Save, ShieldCheck, MapPin, BarChart3, Pencil } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, post, put, upload, getStoredUser } from "@/utils/service";
import { apiMessage, label, shortDate } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, darkInput, darkLabel, darkPrimary, darkSecondary } from "@/components/crm/FloatingDrawer";
import ListShell, { toolbarInput, pill, Avatar } from "@/components/crm/ListShell";
import { useCounts, countText } from "@/components/crm/useCounts";
import { useRemoteList } from "@/components/crm/useRemoteList";
import { countryName, countryOptions, flag } from "@/components/platform/countries";
import { Panel, Switch, input, primary, Field } from "../organization/shared";
import PlatformDashboard from "./PlatformDashboard";
import PlatformAnalytics from "./PlatformAnalytics";
import AppSelect from "@/components/ui/app-select";
import LogoUploader from "@/components/ui/logo-uploader";
import SearchInput from "@/components/ui/search-input";

interface OrgRow { id: string; name: string; slug: string; status: string; base_currency: string; email: string | null; country: string | null; city: string | null; memberships_count: number; created_at: string; suspension_reason: string | null; logo_url: string | null }
interface UserRow {
  id: string; first_name: string; last_name: string; email: string; avatar_url: string | null; is_active: boolean; is_platform_admin: boolean; last_login_at: string | null;
  memberships: Array<{ organization: { id: string; name: string } | null; role: { name: string } | null; status: string }>;
}

const COUNTRIES = countryOptions();

function CountrySelect({ value, onChange, className }: { value: string; onChange: (v: string) => void; className: string }) {
  return (
    <AppSelect className={className} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Select a country</option>
      {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
    </AppSelect>
  );
}

function Organizations() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [orgStatus, setOrgStatus] = useState("");
  const [country, setCountry] = useState("");
  const list = useRemoteList<OrgRow>("/platform/organizations", { search: query, status: orgStatus || undefined, country: country || undefined });
  const { counts, loading: countsLoading } = useCounts("/platform/organizations", { all: {}, active: { status: "active" }, suspended: { status: "suspended" } }, list.rows.length);
  const [creating, setCreating] = useState(params.get("new") === "1");
  const blank = { name: "", base_currency: "UGX", country: "", city: "", owner_email: "", owner_first_name: "", owner_last_name: "" };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState<OrgRow | null>(null);
  const [location, setLocation] = useState({ country: "", city: "" });
  const [busy, setBusy] = useState(false);
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!newLogo) { setLogoPreview(null); return; }
    const url = URL.createObjectURL(newLogo);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [newLogo]);
  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const closeCreate = () => { setCreating(false); if (params.get("new")) setParams({}, { replace: true }); };
  const create = async () => {
    setBusy(true);
    try {
      const r = await post<{ data: { organization: OrgRow } }>("/platform/organizations", { ...form, country: form.country || null, city: form.city || null });
      if (newLogo) {
        const fd = new FormData();
        fd.append("logo", newLogo);
        await upload(`/platform/organizations/${r.data.organization.id}/logo`, fd).catch((e) => notify(apiMessage(e, "The organization was created, but its logo could not be uploaded.")));
      }
      notify(`Organization created. ${form.owner_email} has been invited as its owner.`);
      setForm(blank);
      setNewLogo(null);
      closeCreate();
      list.reload();
    } catch (e) { notify(apiMessage(e, "Could not create the organization.")); }
    finally { setBusy(false); }
  };
  const saveLocation = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      await put(`/platform/organizations/${editing.id}`, { country: location.country || null, city: location.city || null });
      notify(`${editing.name}'s location saved`);
      setEditing(null);
      list.reload();
    } catch (e) { notify(apiMessage(e, "Could not save the location.")); }
    finally { setBusy(false); }
  };
  const setStatus = async (o: OrgRow, status: string) => {
    const reason = status === "suspended" ? window.prompt(`Why is ${o.name} being suspended? Its members will be unable to sign in to it.`) : null;
    if (status === "suspended" && !reason) return;
    try { await put(`/platform/organizations/${o.id}`, { status, suspension_reason: reason }); notify(`${o.name} ${status === "active" ? "reactivated" : "suspended"}`); list.reload(); }
    catch (e) { notify(apiMessage(e, "Could not change the status.")); }
  };

  const card = (o: OrgRow) => (
    <div className="flex h-full flex-col">
      <div className="flex items-start gap-3">
        {o.logo_url
          ? <img src={o.logo_url} alt="" className="h-10 w-10 shrink-0 rounded-lg border border-gray-100 bg-white object-contain p-1" />
          : <Avatar name={o.name} className="rounded-lg" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{o.name}</p>
          <p className="flex items-center gap-1 truncate text-xs text-gray-500"><MapPin className="h-3 w-3 shrink-0" />{flag(o.country)} {[o.city, o.country ? countryName(o.country) : null].filter(Boolean).join(", ") || "Location not set"}</p>
        </div>
        <span className={pill(o.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>{label(o.status)}</span>
      </div>
      {o.status !== "active" && o.suspension_reason && <p className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700">Suspended: {o.suspension_reason}</p>}
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg bg-gray-50 py-2"><p className="text-sm font-semibold text-gray-900">{o.memberships_count}</p><p className="text-[11px] text-gray-500">Members</p></div>
        <div className="rounded-lg bg-gray-50 py-2"><p className="text-sm font-semibold text-gray-900">{o.base_currency}</p><p className="text-[11px] text-gray-500">Currency</p></div>
        <div className="rounded-lg bg-gray-50 py-2"><p className="text-sm font-semibold text-gray-900">{shortDate(o.created_at)}</p><p className="text-[11px] text-gray-500">Created</p></div>
      </div>
      <div className="min-h-[1rem] flex-1" />
      <div className="grid grid-cols-3 gap-2 pt-3">
        <button onClick={() => navigate(`/platform/analytics?org=${o.id}`)} className="inline-flex items-center justify-center gap-1 rounded-lg bg-primary px-2 py-1.5 text-xs text-white hover:bg-primary/90"><BarChart3 className="h-3.5 w-3.5" />Analytics</button>
        <button onClick={() => navigate(`/platform/users?organization_id=${o.id}`)} className="inline-flex items-center justify-center gap-1 rounded-lg border border-gray-300 px-2 py-1.5 text-xs hover:bg-gray-50"><Users className="h-3.5 w-3.5" />Users</button>
        <button onClick={() => setStatus(o, o.status === "active" ? "suspended" : "active")} className={`rounded-lg border px-2 py-1.5 text-xs hover:bg-gray-50 ${o.status === "active" ? "border-red-200 text-red-600" : "border-green-200 text-green-700"}`}>{o.status === "active" ? "Suspend" : "Reactivate"}</button>
      </div>
      <button onClick={() => { setEditing(o); setLocation({ country: o.country ?? "", city: o.city ?? "" }); }} className="mt-2 inline-flex items-center justify-center gap-1 text-[11px] text-gray-500 hover:text-gray-900"><Pencil className="h-3 w-3" />Edit logo & location</button>
    </div>
  );

  return (
    <div className="space-y-5 p-4 sm:p-0">
      <PageHeader title="Organizations" description="Create, locate, suspend and reactivate organizations" />
      <ListShell renderCard={card} rowKey={(o) => o.id} rows={list.rows} loading={list.loading} pagination={list.pagination} onPage={list.setPage} onPerPage={list.setPerPage} noun="organizations"
        grid={{
          id: "platform-orgs",
          search: <SearchInput className="w-full sm:w-64" placeholder="Search organizations…" value={search} onChange={(e) => setSearch(e.target.value)} />,
          filters: (<>
            <label className="text-xs text-gray-600">Status
              <AppSelect className={`${toolbarInput} mt-1`} value={orgStatus} onChange={(e) => setOrgStatus(e.target.value)}><option value="">All</option><option value="active">Active</option><option value="suspended">Suspended</option></AppSelect>
            </label>
            <label className="text-xs text-gray-600">Country
              <CountrySelect className={`${toolbarInput} mt-1`} value={country} onChange={setCountry} />
            </label>
          </>),
          activeFilters: (orgStatus ? 1 : 0) + (country ? 1 : 0),
          onClearFilters: () => { setOrgStatus(""); setCountry(""); },
          stats: [
            { label: "Organizations", value: countText(counts.all) },
            { label: "Active", value: countText(counts.active), tone: "good" },
            { label: "Suspended", value: countText(counts.suspended), tone: counts.suspended ? "bad" : "default" },
          ],
          statsLoading: countsLoading,
        }}
        onAdd={() => setCreating(true)} addLabel="New Organization"
        empty={{ icon: Building2, title: "No organizations", text: "Create an organization and invite its owner.", action: "Create First Organization" }} />

      <FloatingDrawer isOpen={creating} onClose={closeCreate}>
        <DrawerHeader title="New Organization" subtitle="The owner receives an email invitation and sets up the rest" />
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div><label className={darkLabel}>Organization name *</label><input className={darkInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <label className={darkLabel}>Logo</label>
            <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-gray-700 bg-gray-900 px-3 py-3 text-xs text-gray-400 hover:border-gray-500">
              {logoPreview ? <img src={logoPreview} alt="" className="h-8 max-w-[6rem] object-contain" /> : <Building2 className="h-5 w-5" />}
              <span className="flex-1 truncate">{newLogo ? newLogo.name : "Optional. PNG, JPG or WebP, up to 2 MB"}</span>
              <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => setNewLogo(e.target.files?.[0] ?? null)} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={darkLabel}>Country</label><CountrySelect className={darkInput} value={form.country} onChange={(v) => setForm({ ...form, country: v })} /></div>
            <div><label className={darkLabel}>City</label><input className={darkInput} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Kampala" /></div>
          </div>
          <div><label className={darkLabel}>Base currency</label><input maxLength={3} className={darkInput} value={form.base_currency} onChange={(e) => setForm({ ...form, base_currency: e.target.value.toUpperCase() })} /></div>
          <div><label className={darkLabel}>Owner email *</label><input type="email" className={darkInput} value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={darkLabel}>Owner first name</label><input className={darkInput} value={form.owner_first_name} onChange={(e) => setForm({ ...form, owner_first_name: e.target.value })} /></div>
            <div><label className={darkLabel}>Owner last name</label><input className={darkInput} value={form.owner_last_name} onChange={(e) => setForm({ ...form, owner_last_name: e.target.value })} /></div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-800 space-y-3">
          <button onClick={create} disabled={busy || !form.name || !form.owner_email} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Creating..." : "Create & Invite Owner"}</button>
          <button onClick={closeCreate} className={darkSecondary}>Cancel</button>
        </div>
      </FloatingDrawer>

      <FloatingDrawer isOpen={!!editing} onClose={() => setEditing(null)}>
        <DrawerHeader title="Edit Organization" subtitle={editing?.name} />
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {editing && (
            <div>
              <label className={darkLabel}>Logo</label>
              <LogoUploader<OrgRow> dark logoUrl={editing.logo_url} name={editing.name} endpoint={`/platform/organizations/${editing.id}/logo`}
                onChange={(o) => { setEditing({ ...editing, logo_url: o.logo_url }); list.reload(); }} />
            </div>
          )}
          <p className="pt-2 text-xs text-gray-400">The location is used for the popularity map and country analytics.</p>
          <div><label className={darkLabel}>Country</label><CountrySelect className={darkInput} value={location.country} onChange={(v) => setLocation({ ...location, country: v })} /></div>
          <div><label className={darkLabel}>City</label><input className={darkInput} value={location.city} onChange={(e) => setLocation({ ...location, city: e.target.value })} /></div>
        </div>
        <div className="p-6 border-t border-gray-800 space-y-3">
          <button onClick={saveLocation} disabled={busy} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Saving..." : "Save Location"}</button>
          <button onClick={() => setEditing(null)} className={darkSecondary}>Cancel</button>
        </div>
      </FloatingDrawer>
    </div>
  );
}

function PlatformUsers() {
  const me = getStoredUser();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("");
  const [admins, setAdmins] = useState("");
  const organizationId = params.get("organization_id") ?? "";
  const [orgs, setOrgs] = useState<Array<{ id: string; name: string }>>([]);
  const list = useRemoteList<UserRow>("/platform/users", { search: query, active: active || undefined, platform_admin: admins || undefined, organization_id: organizationId || undefined });
  const { counts, loading: countsLoading } = useCounts("/platform/users", { all: {}, active: { active: "1" }, inactive: { active: "0" }, admins: { platform_admin: "1" } }, list.rows.length);
  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => { get<{ data: Array<{ id: string; name: string }> }>("/platform/organizations", { page_size: 100 }).then((r) => setOrgs(r.data)).catch(() => setOrgs([])); }, []);
  const setOrg = (id: string) => setParams(id ? { organization_id: id } : {}, { replace: true });

  const update = async (u: UserRow, patch: Record<string, boolean>, done: string) => {
    try { await put(`/platform/users/${u.id}`, patch); notify(done); list.reload(); }
    catch (e) { notify(apiMessage(e, "Could not update the user.")); }
  };
  const revoke = async (u: UserRow) => {
    if (!await confirmDialog(`Sign ${u.first_name} out of every device?`)) return;
    try { await post(`/platform/users/${u.id}/revoke-sessions`, {}); notify("Signed out everywhere"); }
    catch (e) { notify(apiMessage(e, "Could not end the sessions.")); }
  };

  const columns: ColumnDef<UserRow>[] = [
    { id: "name", header: "User", cell: ({ row }) => (<div className="flex items-center gap-2.5"><Avatar name={`${row.original.first_name} ${row.original.last_name}`} src={row.original.avatar_url} className="h-8 w-8 text-[10px]" /><div><p className="text-xs font-semibold text-gray-900">{row.original.first_name} {row.original.last_name}{row.original.is_platform_admin && <ShieldCheck className="ml-1 inline w-3 h-3 text-primary" />}</p><p className="text-xs text-gray-500">{row.original.email}</p></div></div>) },
    {
      id: "orgs", header: "Organization", cell: ({ row }) => row.original.is_platform_admin
        ? <span className={pill("bg-gray-900 text-white")}>System administrator</span>
        : row.original.memberships.length ? (
          <div className="space-y-0.5">{row.original.memberships.map((m, i) => (
            <p key={i} className="text-xs"><span className="font-medium text-gray-900">{m.organization?.name ?? "—"}</span> <span className="text-gray-500">· {m.role?.name ?? "No role"}{m.status !== "active" ? ` · ${label(m.status)}` : ""}</span></p>
          ))}</div>
        ) : <span className="text-xs text-amber-600">No organization</span>,
    },
    { id: "login", header: "Last sign-in", cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.last_login_at ? shortDate(row.original.last_login_at) : "Never"}</span> },
    { id: "status", header: "Status", cell: ({ row }) => <span className={pill(row.original.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800")}>{row.original.is_active ? "Active" : "Deactivated"}</span> },
    {
      id: "actions", header: "Actions", cell: ({ row }) => row.original.id === me?.id ? <span className="text-xs text-gray-400">You</span> : (
        <div className="flex gap-2">
          <button onClick={() => update(row.original, { is_active: !row.original.is_active }, row.original.is_active ? "User deactivated" : "User reactivated")} className={`rounded-lg border px-2 py-1 text-xs hover:bg-gray-50 ${row.original.is_active ? "border-red-200 text-red-600" : "border-green-200 text-green-700"}`}>{row.original.is_active ? "Deactivate" : "Reactivate"}</button>
          <button onClick={() => revoke(row.original)} className="rounded-lg border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50">Sign out everywhere</button>
          {(row.original.is_platform_admin || row.original.memberships.length === 0) && (
            <button onClick={() => update(row.original, { is_platform_admin: !row.original.is_platform_admin }, row.original.is_platform_admin ? "No longer a system administrator" : "Now a system administrator")} className="rounded-lg border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50">{row.original.is_platform_admin ? "Remove admin" : "Make admin"}</button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-4 sm:p-0">
      <PageHeader title="Users" description="Everyone on the platform and the organization they belong to" />
      <ListShell columns={columns} rows={list.rows} loading={list.loading} pagination={list.pagination} onPage={list.setPage} onPerPage={list.setPerPage} noun="users"
        grid={{
          id: "platform-users",
          search: <SearchInput className="w-full sm:w-64" placeholder="Search name or email…" value={search} onChange={(e) => setSearch(e.target.value)} />,
          view: (
            <AppSelect className={`${toolbarInput} max-w-[14rem]`} value={organizationId} onChange={(e) => setOrg(e.target.value)} aria-label="Organization">
              <option value="">All organizations</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </AppSelect>
          ),
          filters: (<>
            <label className="text-xs text-gray-600">Status
              <AppSelect className={`${toolbarInput} mt-1`} value={active} onChange={(e) => setActive(e.target.value)}><option value="">Everyone</option><option value="1">Active</option><option value="0">Deactivated</option></AppSelect>
            </label>
            <label className="text-xs text-gray-600">Account type
              <AppSelect className={`${toolbarInput} mt-1`} value={admins} onChange={(e) => setAdmins(e.target.value)}><option value="">All users</option><option value="1">System administrators</option><option value="0">Organization users</option></AppSelect>
            </label>
          </>),
          activeFilters: (active ? 1 : 0) + (admins ? 1 : 0),
          onClearFilters: () => { setActive(""); setAdmins(""); },
          stats: [
            { label: "All users", value: countText(counts.all) },
            { label: "Active", value: countText(counts.active), tone: "good" },
            { label: "Deactivated", value: countText(counts.inactive), tone: counts.inactive ? "bad" : "default" },
            { label: "System administrators", value: countText(counts.admins) },
          ],
          statsLoading: countsLoading,
        }}
        empty={{ icon: Users, title: "No users found", text: "Try another search or filter." }} />
    </div>
  );
}

function PlatformAudit() {
  const list = useRemoteList<{ id: string; action: string; table_name: string | null; description: string; created_at: string; organization_id: string | null; user?: { first_name: string; last_name: string } | null }>("/platform/audit-logs", {}, 30);
  const columns: ColumnDef<any>[] = [
    { accessorKey: "created_at", header: "When", cell: ({ row }) => <span className="text-xs text-gray-600">{new Date(row.original.created_at).toLocaleString()}</span> },
    { accessorKey: "action", header: "Action", cell: ({ row }) => <span className="text-xs font-medium">{label(String(row.original.action).toLowerCase())}</span> },
    { accessorKey: "description", header: "What happened", cell: ({ row }) => <span className="text-xs text-gray-700">{row.original.description}</span> },
    { id: "who", header: "By", cell: ({ row }) => <span className="text-xs text-gray-600">{row.original.user ? `${row.original.user.first_name} ${row.original.user.last_name}` : "System"}</span> },
  ];
  return <div className="space-y-5 p-4 sm:p-0"><PageHeader title="Activity Log" description="Sign-ins and administrative changes across the platform" /><ListShell columns={columns} rows={list.rows} loading={list.loading} pagination={list.pagination} onPage={list.setPage} onPerPage={list.setPerPage} noun="entries"
    grid={{ id: "platform-audit" }}
    empty={{ icon: Activity, title: "Nothing logged yet", text: "Sign-ins and changes across all organizations appear here." }} /></div>;
}

function PlatformSettings() {
  const [settings, setSettings] = useState<Record<string, any> | null>(null);
  const [configured, setConfigured] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => get<{ data: { settings: Record<string, any>; oauth_configured: Record<string, boolean> } }>("/platform/settings").then((r) => { setSettings(r.data.settings); setConfigured(r.data.oauth_configured); }), []);
  useEffect(() => { load().catch((e) => notify(apiMessage(e, "Could not load settings."))); }, [load]);
  if (!settings) return <div className="bg-white rounded-lg p-6 h-48 animate-pulse" />;
  const v = (k: string) => settings[k];
  const set = (k: string, val: unknown) => setSettings({ ...settings, [k]: val });

  const save = async () => {
    setBusy(true);
    try {
      await put("/platform/settings", {
        oauth: { google: { enabled: !!v("oauth.google.enabled") }, microsoft: { enabled: !!v("oauth.microsoft.enabled") } },
        organizations: { default_currency: v("organizations.default_currency"), default_timezone: v("organizations.default_timezone") },
        support: { email: v("support.email") || null },
        maintenance: { banner: v("maintenance.banner") || null },
      });
      notify("System settings saved");
      load();
    } catch (e) { notify(apiMessage(e, "Could not save settings.")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 p-4 sm:p-0">
      <PageHeader title="System Settings" description="Sign-in providers and defaults for new organizations" />
      <Panel title="Sign-in with Google and Microsoft" description="Turn on the providers people can use. Each person still connects their own account in Settings → Sign-in Methods.">
        <div className="space-y-4">
          {(["google", "microsoft"] as const).map((p) => (
            <div key={p} className="flex items-start justify-between gap-4">
              <div><h3 className="text-xs font-semibold text-gray-900 mb-1">{p === "google" ? "Google" : "Microsoft"}</h3><p className="text-xs text-gray-500">{configured[p] ? "Credentials are configured on the server." : "Credentials are missing on the server (set them in the environment before turning this on)."}</p></div>
              <Switch disabled={!configured[p]} checked={!!v(`oauth.${p}.enabled`)} onChange={(val) => set(`oauth.${p}.enabled`, val)} />
            </div>
          ))}
        </div>
      </Panel>
      <Panel title="Defaults & support">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Default currency for new organizations"><input className={input} maxLength={3} value={v("organizations.default_currency") ?? ""} onChange={(e) => set("organizations.default_currency", e.target.value.toUpperCase())} /></Field>
          <Field label="Default time zone"><input className={input} value={v("organizations.default_timezone") ?? ""} onChange={(e) => set("organizations.default_timezone", e.target.value)} /></Field>
          <Field label="Support email"><input className={input} value={v("support.email") ?? ""} onChange={(e) => set("support.email", e.target.value)} /></Field>
          <Field label="Maintenance message" hint="Shown to everyone while set"><input className={input} value={v("maintenance.banner") ?? ""} onChange={(e) => set("maintenance.banner", e.target.value)} /></Field>
        </div>
        <div className="mt-6 flex justify-end"><button onClick={save} disabled={busy} className={primary}>{busy ? "Saving..." : "Save Changes"}</button></div>
      </Panel>
    </div>
  );
}

/** The system administrator's console. The section comes from the sidebar route (/platform/:section). */
export default function PlatformPage() {
  const { section } = useParams();
  switch (section) {
    case "organizations": return <Organizations />;
    case "users": return <PlatformUsers />;
    case "analytics": return <PlatformAnalytics />;
    case "activity": return <PlatformAudit />;
    case "settings": return <PlatformSettings />;
    default: return <PlatformDashboard />;
  }
}
